import type { SegmentAudienceRefusal } from "#shared/segmentAudienceRefusal.ts";
import { ORPCError } from "@orpc/client";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useQuery } from "@tanstack/react-query";
import { useSegmentsClient } from "../../api/segmentsQuery.ts";

export interface SegmentAudienceRefusalState {
    refusal: SegmentAudienceRefusal | null;
    isPending: boolean;
}

export interface UseSegmentAudienceRefusalOptions {
    isEnabled: boolean;
    segmentId: string | null;
}

export function useSegmentAudienceRefusal({ isEnabled, segmentId }: UseSegmentAudienceRefusalOptions): SegmentAudienceRefusalState {
    const session = useSession();
    const { queryUtils: segmentsQueryUtils } = useSegmentsClient();
    const organizationId = session?.organizationId ?? "";
    const isResolutionEnabled = isEnabled && !!segmentId && !!organizationId;

    const {
        data: segment,
        error,
        isPending
    } = useQuery(
        createQueryOptions(segmentsQueryUtils.getSegment.queryOptions({ input: { orgId: organizationId, segmentId: segmentId ?? "" } }), {
            enabled: isResolutionEnabled,
            throwOnError: false
        })
    );

    if (isResolutionEnabled) {
        if (isPending) {
            return { refusal: null, isPending: true };
        }

        if (error instanceof ORPCError && error.code === "NOT_FOUND") {
            return { refusal: "segmentNotVisible", isPending: false };
        }

        if (segment) {
            if (!segment.isRuleBased) {
                return { refusal: "handPickedSegment", isPending: false };
            }

            if (segment.hasUnlistedRules) {
                return { refusal: "brokenRules", isPending: false };
            }

            if (segment.matchType === "any") {
                return { refusal: "unsupportedCapability", isPending: false };
            }
        }
    }

    return { refusal: null, isPending: false };
}
